import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

import { useApolloClient } from '@apollo/react-hooks';
import { DocumentNode } from 'graphql';

import { IBaseProps } from '@rbi-ctg/frontend';
import { MenuObject, SanityMenuObject } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import {
  GetComboDocument,
  GetItemDocument,
  GetPickerDocument,
  GetSectionDocument,
  PosDataDocument,
} from 'generated/sanity-graphql';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useForceUpdate } from 'hooks/use-force-update';
import { fixTypes } from 'remote/api/menu-objects';
import { MenuDataError, NotFoundMenuItemError } from 'remote/exceptions';
import { useDayPartContext } from 'state/day-part';
import { useLocale } from 'state/intl';
import { useLogger } from 'state/logger';
import { useMenuContext } from 'state/menu';
import { useNetworkContext } from 'state/network';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import getRestaurantPosDataPlus from 'state/store/hooks/get-restaurant-pos-data-plus';
import { transformPlus } from 'utils/menu';
import { parseStringifiedJSON } from 'utils/parse-string';
import { PosVendors } from 'utils/vendor-config';

import { applyFiltersForAvailability } from './apply-filters-for-availability';
import { filterForStaticMenu } from './filter-for-static-menu';
import usePricingFunction from './hooks/use-pricing-function';

interface ICheckItemAvailability {
  posVendor: string;
  itemId: string;
  restaurantId: string;
}

export type IMenuOptionsContext = {
  getMenuObject(id: string): IState;
  asyncGetMenuObject(id: string, customFilter?: ICustomFilter): Promise<IState>;
  filterForAvailability(
    menuData: SanityMenuObject[] | SanityMenuObject
  ): MenuObject | MenuObject[] | null;
  checkItemAvailability(args: ICheckItemAvailability): Promise<boolean>;
} & ReturnType<typeof usePricingFunction>;

export interface IState {
  data: MenuObject | null;
  loading: boolean;
}
interface ICustomFilter {
  (data: SanityMenuObject): MenuObject | MenuObject[] | null;
}

export const MenuOptionsContext = createContext<IMenuOptionsContext>({} as IMenuOptionsContext);
export const useMenuOptionsContext = () => useContext(MenuOptionsContext);
const useSanityGqlEndpoint = () => useNetworkContext().sanityEndpoints.graphql;

export default MenuOptionsContext.Consumer;

export const MenuOptionsProvider = ({ children }: IBaseProps) => {
  const { activeDayParts, dayParts } = useDayPartContext();
  const logger = useLogger();
  const { prices, store } = useStoreContext();
  const { vendor }: { vendor: PosVendors } = usePosVendor() as { vendor: PosVendors };
  const menu = useRef<Record<string, IState>>({});
  const { locale } = useLocale();
  const client = useApolloClient();
  const uri = useSanityGqlEndpoint();
  const { showStaticMenu } = useMenuContext();
  const { serviceMode } = useServiceModeContext();

  // a small hack until our menu data store is more reactive
  // forces a re-render upon successful menu data load
  const forceUpdate = useForceUpdate();

  const filterForAvailability = applyFiltersForAvailability({
    activeDayParts,
    dayParts,
    prices,
    vendor,
  });

  const filterForAvailabilityMemo = useCallback(filterForAvailability, [filterForAvailability]);

  const filterData = showStaticMenu ? filterForStaticMenu : filterForAvailabilityMemo;

  const {
    priceForItemOptionModifier,
    pricingFunction,
    priceForModifierSelections,
  } = usePricingFunction({
    logger,
    prices,
    vendor,
  });

  const setMenuData = useCallback(
    (menuData, menuDataLoading, itemID, skipForceUpdate = false, customFilter?: ICustomFilter) => {
      const fixedData = menuData && fixTypes(menuData);
      const filter = customFilter || filterData;

      menu.current = {
        ...menu.current,
        [itemID]: {
          loading: menuDataLoading,
          data: filter(fixedData),
          id: itemID,
        },
      };
      if (!skipForceUpdate) {
        forceUpdate();
      }
    },
    [filterData, forceUpdate]
  );

  const getItemIdAndType = useCallback((id: string) => {
    const [type, ...idSplit] = id.split('-');
    const itemID = idSplit.join('-');
    return { type, itemID };
  }, []);

  const maybeGetCurrentItem = useCallback(
    (itemID: string, customFilter?: ICustomFilter) => {
      const { current: currentMenu } = menu;

      const filter = customFilter || filterData;

      if (currentMenu[itemID]) {
        currentMenu[itemID] = {
          ...currentMenu[itemID],
          data:
            currentMenu[itemID].data && filter(currentMenu[itemID].data as SanityMenuObject)
              ? currentMenu[itemID]?.data
              : null,
        };

        return currentMenu[itemID];
      }
      return undefined;
    },
    [filterData]
  );
  /**
   *  id is a bit of a misnomer. This is the actually
   *  `${MenuObjectTypes}-${sanityId}`
   *  A CartEntryType can be mapped to MenuObjectTypes
   *  by transforming to camelCase
   *  e.g ComboSlot -> comboSlot
   */
  const getMenuObject = useCallback(
    (id: string) => {
      const { type, itemID } = getItemIdAndType(id);
      const currentMenuItem = maybeGetCurrentItem(itemID);
      if (currentMenuItem) {
        return currentMenuItem;
      }

      const queryMenuData = (menuItemId: string, query: DocumentNode) => {
        client
          .query({
            fetchPolicy: 'no-cache',
            context: { uri },
            query,
            variables: {
              id: menuItemId,
            },
          })
          .then(res => {
            const capitalizedType = type[0].toUpperCase() + type.slice(1);
            const data = res?.data;
            setMenuData(data?.[capitalizedType], data?.loading, menuItemId);
          })
          .catch(err => {
            throw new MenuDataError(`An error occurred loading menu ${type}`, err);
          });
      };

      switch (type) {
        case MenuObjectTypes.ITEM:
          queryMenuData(itemID, GetItemDocument);
          break;
        case MenuObjectTypes.COMBO:
          queryMenuData(itemID, GetComboDocument);
          break;
        case MenuObjectTypes.PICKER:
          queryMenuData(itemID, GetPickerDocument);
          break;
        case MenuObjectTypes.SECTION:
          queryMenuData(itemID, GetSectionDocument);
          break;
        default:
          throw new NotFoundMenuItemError(type);
      }

      return menu.current[itemID] || { loading: true, data: null };
    },
    [getItemIdAndType, maybeGetCurrentItem, client, uri, setMenuData]
  );

  /**
   * Like getMenuObject but async instead of reactive
   */
  const asyncGetMenuObject = useCallback(
    async (id: string, customFilter?: ICustomFilter) => {
      const { type, itemID } = getItemIdAndType(id);
      const currentMenuItem = maybeGetCurrentItem(itemID, customFilter);
      if (currentMenuItem) {
        return currentMenuItem;
      }
      const queryMenuData = async (menuItemId: string, query: DocumentNode) => {
        try {
          const res = await client.query({
            fetchPolicy: 'no-cache',
            context: { uri },
            query,
            variables: {
              id: menuItemId,
            },
          });
          const capitalizedType = type[0].toUpperCase() + type.slice(1);
          const data = res?.data;
          setMenuData(data?.[capitalizedType], data?.loading, menuItemId, true, customFilter);
        } catch (err) {
          throw new MenuDataError(`An error occurred loading menu ${type}`, err);
        }
      };
      const queryDocument = {
        [MenuObjectTypes.ITEM]: GetItemDocument,
        [MenuObjectTypes.COMBO]: GetComboDocument,
        [MenuObjectTypes.PICKER]: GetPickerDocument,
        [MenuObjectTypes.SECTION]: GetSectionDocument,
      }[type];

      if (queryDocument) {
        await queryMenuData(itemID, queryDocument);
      } else {
        throw new NotFoundMenuItemError(type);
      }

      return menu.current[itemID];
    },
    [client, getItemIdAndType, maybeGetCurrentItem, setMenuData, uri]
  );

  const checkItemAvailability = useCallback(
    async ({ posVendor, itemId, restaurantId }: ICheckItemAvailability) => {
      try {
        const { data: posData } = await client.query({
          context: { uri },
          query: PosDataDocument,
          variables: {
            id: restaurantId,
          },
        });

        const rawPlus = getRestaurantPosDataPlus(posData, serviceMode);

        const isItemAvailable = async (plus: string) => {
          const parsedPlus = parseStringifiedJSON({ defaultValue: {}, value: plus });
          const availabilityFilter = applyFiltersForAvailability({
            activeDayParts,
            dayParts,
            prices: transformPlus(parsedPlus),
            vendor: posVendor as PosVendors,
          });
          const { data } = await asyncGetMenuObject(itemId, availabilityFilter);

          const optionsAvailableForPicker = (menuData: MenuObject) => {
            // If selected item is picker, make sure there are at least some options available.
            const notPicker = menuData?._type !== MenuObjectTypes.PICKER;
            const optionsAvailable = () => Boolean(menuData?.options?.length);
            return notPicker || optionsAvailable();
          };

          return !!data && optionsAvailableForPicker(data);
        };

        return !!rawPlus && isItemAvailable(rawPlus);
      } catch (err) {
        throw new NotFoundMenuItemError(itemId);
      }
    },
    [activeDayParts, asyncGetMenuObject, client, dayParts, serviceMode, uri]
  );

  // There are a few specs that cause us to force remove all items.
  //
  // 1. When the store changes, we have to remove all loaded items.
  //    This is because a store might have different menu data.
  // 2. When then language changes, we wipe the data so that the user
  //    refetches the data and then gets it in the new language
  useEffect(() => {
    menu.current = {};
  }, [locale, store]);

  return (
    <MenuOptionsContext.Provider
      value={{
        getMenuObject,
        priceForItemOptionModifier,
        pricingFunction,
        priceForModifierSelections,
        filterForAvailability,
        asyncGetMenuObject,
        checkItemAvailability,
      }}
    >
      {children}
    </MenuOptionsContext.Provider>
  );
};
